<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';
import { CountdownType, ThemeType } from '../../types/assessment';
import { audioManager } from '../../utils/audioManager';
import Slide from '../Animation/Slide.vue';
import secondCount from '../../assets/audios/quiz_second_count.wav';
import lastSecond from '../../assets/audios/quiz_countdown_last_second.wav';
import timeRunningOut from '../../assets/audios/quiz_time_runing_out.wav';
import { QuizAudioKey } from '../../types/quiz';

interface Props {
    type: CountdownType;
    duration: number;
    current?: number;
    theme?: ThemeType;
}

const props = withDefaults(defineProps<Props>(), {
    type: CountdownType.BAR,
    duration: 3,
    theme: ThemeType.PRIMARY,
});
const emit = defineEmits(['finished', 'on-tick']);

const countdown = ref<number>(props.current ?? props.duration);
const isCountingDown = ref<boolean>(false);
const percentage = ref<number>(100);
let countdownInterval: NodeJS.Timeout | null = null;

const startCountdown = () => {
    isCountingDown.value = true;
    if (props.type === CountdownType.CIRCULAR) {
        audioManager.play(secondCount, QuizAudioKey.SFX, { loop: false });
    }
    countdownInterval = setInterval(() => {
        if (countdown.value === 0) {
            clearInterval(countdownInterval!);
            countdownInterval = null;
            isCountingDown.value = false;
            emit('finished');
            return;
        }
        countdown.value--;
        percentage.value = (countdown.value / props.duration) * 100;
        if (props.type === CountdownType.CIRCULAR) {
            audioManager.play(countdown.value > 0 ? secondCount : lastSecond, QuizAudioKey.SFX, { loop: false });
        }
    }, 1000);
};
watchEffect(() => {
    if (props.type === CountdownType.BAR && percentage.value === 10) {
        audioManager.play(timeRunningOut, QuizAudioKey.SFX, { loop: false });
    }
})

watch(() => countdown.value, (val) => {
    emit('on-tick', val);
})


onMounted(() => {
    startCountdown();
});

onBeforeUnmount(() => {
    if (countdownInterval) {
        clearInterval(countdownInterval);
    }
    audioManager.stop(QuizAudioKey.SFX);
});
</script>

<template>
    <!-- Circular Progress -->
    <div class="relative size-40" v-if="props.type === CountdownType.CIRCULAR">
        <svg class="size-full -rotate-90" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
            <!-- Background Circle -->
            <circle cx="18" cy="18" r="16" fill="white" class="stroke-current text-orange-100 dark:text-violet-400" stroke-width="2">
            </circle>
            <!-- Progress Circle -->
            <circle cx="18" cy="18" r="16" fill="none" class="stroke-current transistion-transform duration-[1s] ease-linear text-orange-600 dark:text-violet-700" stroke-width="3" :stroke-dasharray="100" :stroke-dashoffset="100 - percentage">
            </circle>
        </svg>
        <!-- Countdown Text -->
        <div class="absolute top-1/2 start-1/2 transform -translate-y-1/2 -translate-x-1/2 flex">
            <Slide direction="down" v-for="second in countdown.toString().split('')">
                <div class="text-center font-bold text-7xl text-orange-600 dark:text-violet-700" :key="second">{{ second }}
                </div>
            </Slide>
        </div>
    </div>
    <!-- End Circular Progress -->

    <!-- Bar Component -->
    <div class="left-0 top-0 w-full bg-black/30 h-4 rounded-full overflow-hidden" v-if="props.type === CountdownType.BAR">
        <div class="transistion duration-[1s] ease-linear bg-gradient-to-r from-orange-500  to-yellow-500 dark:bg-fuchsia-500  h-4 rounded-full" :style="`width: ${((countdown) * 100) / (props.duration) | 0}%`"></div>
    </div>
    <!-- End Bar Component -->
</template>